import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowRight, MessageCircle, BadgeCheck, Star, Quote } from "lucide-react";
import api from "../lib/api";
import { useSite } from "../context/SiteContext";
import { waLink } from "../lib/site";
import SectionHead from "../components/site/SectionHead";
import LeadForm from "../components/site/LeadForm";
import ProductCard from "../components/site/ProductCard";
import EMICalculator from "../components/site/EMICalculator";
import EligibilityChecker from "../components/site/EligibilityChecker";
import FAQAccordion from "../components/site/FAQAccordion";

const fade = (i = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.55, delay: i * 0.08 },
});

export default function Home() {
  const { settings, products, partners } = useSite();
  const [faqs, setFaqs] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [blogs, setBlogs] = useState([]);

  useEffect(() => {
    api.get("/faqs").then((r) => setFaqs(r.data.slice(0, 6))).catch(() => {});
    api.get("/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
    api.get("/blog").then((r) => setBlogs(r.data.slice(0, 3))).catch(() => {});
  }, []);

  const hero = settings.hero || {};

  return (
    <div data-testid="home-page">
      {/* announcement bar */}
      {settings.announcement && (
        <div className="bg-brand-dark text-white/80 text-center text-xs py-2.5 px-4" data-testid="announcement-bar">
          {settings.announcement}
        </div>
      )}

      {/* HERO */}
      <section className="container-site pt-10 pb-16 lg:pt-16 lg:pb-24">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <motion.div className="lg:col-span-6" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="eyebrow flex items-center gap-2" data-testid="hero-eyebrow">
              <span className="w-6 h-px bg-brand-gold inline-block" /> {hero.eyebrow}
            </p>
            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-[3.6rem] font-extrabold leading-[1.06] text-brand-dark" data-testid="hero-title">
              Find the Right Loan with <span className="text-brand-red">Expert Guidance</span>
            </h1>
            <p className="mt-6 text-base md:text-lg text-brand-muted leading-relaxed max-w-xl" data-testid="hero-subtitle">{hero.subtitle}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/apply" data-testid="hero-apply-btn" className="btn-primary">Apply Now <ArrowRight size={16} /></Link>
              <Link to="/calculators/eligibility" data-testid="hero-eligibility-btn" className="btn-outline">Check Eligibility</Link>
              <a href={waLink(settings)} target="_blank" rel="noreferrer" data-testid="hero-whatsapp-btn" className="btn-outline !border-[#1FA855]/40 !text-[#1FA855]">
                <MessageCircle size={16} /> WhatsApp
              </a>
            </div>
            <div className="mt-10 flex flex-wrap gap-8" data-testid="hero-stats">
              {(settings.stats || []).map((s, i) => (
                <div key={i}>
                  <p className="font-heading text-2xl sm:text-3xl font-extrabold text-brand-dark">{s.value}</p>
                  <p className="text-xs text-brand-muted font-semibold mt-1">{s.label}</p>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div className="lg:col-span-6 relative" initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.15 }}>
            <div className="relative">
              <img src={hero.image} alt="Financial advisor guiding a customer through loan options"
                className="rounded-3xl w-full h-[340px] sm:h-[440px] lg:h-[520px] object-cover object-top max-h-[80vh] md:max-h-none" data-testid="hero-image" />
              <div className="absolute -bottom-6 left-5 sm:left-8 backdrop-blur-xl bg-white/80 border border-white/40 shadow-xl rounded-2xl p-5 max-w-[260px]" data-testid="hero-trust-card">
                <div className="flex items-center gap-2 text-brand-gold"><BadgeCheck size={18} /><span className="font-heading font-bold text-sm text-brand-dark">{hero.card_title}</span></div>
                <p className="text-xs text-brand-muted mt-2 leading-relaxed">{hero.card_text}</p>
              </div>
              <div className="absolute top-6 right-5 sm:right-8 backdrop-blur-xl bg-white/80 border border-white/40 shadow-xl rounded-2xl px-5 py-4 hidden sm:block">
                <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-brand-muted">Approval by</p>
                <p className="font-heading font-bold text-sm text-brand-dark mt-0.5">Lending Partners</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* QUICK ENQUIRY */}
      <section className="container-site pb-20 lg:pb-28" data-testid="quick-enquiry-section">
        <motion.div {...fade()}>
          <LeadForm />
        </motion.div>
      </section>

      {/* PARTNERS */}
      <section className="py-16 lg:py-20 border-y border-black/5 bg-white" data-testid="partners-section">
        <div className="container-site">
          <p className="eyebrow text-center" data-testid="partners-eyebrow">Our Banking & NBFC Partners</p>
        </div>
        <div className="mt-10">
          <Marquee speed={30} gradient={false} pauseOnHover>
            {partners.map((p) => (
              <div key={p.id} data-testid={`partner-${p.short?.toLowerCase().replace(/\s/g, "-")}`}
                className="mx-10 lg:mx-14 flex flex-col items-center justify-center">
                {p.logo_url ? (
                  <img src={p.logo_url} alt={`${p.name} — lending partner logo`} loading="lazy"
                    className="h-9 lg:h-11 w-auto max-w-[150px] object-contain" />
                ) : (
                  <span className="font-heading text-2xl lg:text-3xl font-extrabold tracking-tight text-brand-dark opacity-60 hover:opacity-100 transition-opacity duration-300">{p.short}</span>
                )}
              </div>
            ))}
          </Marquee>
        </div>
        <p className="text-center text-xs text-brand-muted mt-8 px-4">Partner list is indicative and managed by BharatBest Fintech. Final lender selection depends on your eligibility.</p>
      </section>

      {/* PRODUCTS */}
      <section className="container-site py-20 lg:py-28" data-testid="products-section">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
          <SectionHead eyebrow="Loan Products" title="One Marketplace, Every Loan You Need"
            subtitle="Explore financing options based on your requirements, eligibility and available lending partners." />
          <Link to="/loans" data-testid="view-all-products-btn" className="btn-outline shrink-0">View All Products <ArrowRight size={15} /></Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {products.slice(0, 8).map((p, i) => (
            <motion.div key={p.id} {...fade(i)}><ProductCard product={p} /></motion.div>
          ))}
        </div>
      </section>

      {/* WHY BHARATBEST */}
      <section className="bg-white border-y border-black/5 py-20 lg:py-28" data-testid="why-section">
        <div className="container-site">
          <SectionHead eyebrow="Why BharatBest" title="Built on Trust, Choice and Transparency"
            subtitle="A premium, guided way to find financing — without the runaround." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
            {(settings.benefits || []).map((b, i) => (
              <motion.div key={i} {...fade(i)} className="card-site p-7" data-testid={`benefit-card-${i}`}>
                <span className="font-heading text-sm font-extrabold text-brand-gold">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="mt-3 font-heading text-lg font-bold text-brand-dark">{b.title}</h3>
                <p className="mt-2 text-sm text-brand-muted leading-relaxed">{b.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="container-site py-20 lg:py-28" data-testid="how-it-works-section">
        <SectionHead eyebrow="How It Works" title="From Enquiry to Disbursal, Guided"
          subtitle="A simple, transparent process. Final approval is always made by the lending partner." />
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5 mt-12">
          {(settings.steps || []).map((s, i) => (
            <motion.div key={i} {...fade(i)} className="relative card-site p-6" data-testid={`step-card-${i}`}>
              <span className="font-heading text-3xl font-extrabold text-brand-red/15">{s.num}</span>
              <h3 className="mt-3 font-heading text-base font-bold text-brand-dark leading-snug">{s.title}</h3>
              <p className="mt-2 text-xs text-brand-muted leading-relaxed">{s.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ELIGIBILITY */}
      <section className="bg-brand-dark py-20 lg:py-28" data-testid="eligibility-section">
        <div className="container-site">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-4">
              <SectionHead dark eyebrow="Eligibility Checker" title="Know Where You Stand in 30 Seconds"
                subtitle="Get an indicative eligibility estimate based on your profile — before you speak to anyone." />
              <p className="mt-6 text-sm text-white/50 leading-relaxed">Indicative estimate only. Final eligibility, amount and terms are determined by the lending partner after verification.</p>
            </div>
            <div className="lg:col-span-8"><EligibilityChecker /></div>
          </div>
        </div>
      </section>

      {/* EMI */}
      <section className="container-site py-20 lg:py-28" data-testid="emi-section">
        <SectionHead eyebrow="EMI Calculator" title="Plan Your Monthly Payment"
          subtitle="Adjust amount, rate and tenure to see your EMI, total interest and total payable instantly." />
        <div className="mt-12"><EMICalculator /></div>
      </section>

      {/* COMPARE TEASER */}
      <section className="container-site pb-20 lg:pb-28" data-testid="compare-teaser-section">
        <motion.div {...fade()} className="rounded-3xl bg-brand-dark text-white p-8 sm:p-12 grid lg:grid-cols-2 gap-8 items-center">
          <div>
            <p className="eyebrow !text-brand-gold">Loan Comparison</p>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-extrabold leading-tight">Compare Before You Commit</h2>
            <p className="mt-4 text-white/60 text-sm md:text-base leading-relaxed">Put loan products side by side — indicative rates, fees, tenure and eligibility — so you can decide with clarity. Terms remain subject to lender policies.</p>
          </div>
          <div className="flex lg:justify-end">
            <Link to="/compare-loans" data-testid="compare-teaser-btn" className="btn-primary">Compare Loans <ArrowRight size={15} /></Link>
          </div>
        </motion.div>
      </section>

      {/* TESTIMONIALS */}
      {testimonials.length > 0 && (
        <section className="bg-white border-y border-black/5 py-20 lg:py-28" data-testid="testimonials-section">
          <div className="container-site">
            <SectionHead eyebrow="Customer Stories" title="What Our Customers Say"
              subtitle="Real guidance experiences from customers across India." />
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
              {testimonials.map((t, i) => (
                <motion.div key={t.id} {...fade(i)} className="card-site p-7 flex flex-col" data-testid={`testimonial-card-${i}`}>
                  <Quote size={22} className="text-brand-gold" />
                  <p className="mt-4 text-sm text-brand-dark/80 leading-relaxed flex-1">{t.review}</p>
                  <div className="mt-5 pt-4 border-t border-black/5 flex items-center justify-between">
                    <div>
                      <p className="font-heading font-bold text-sm text-brand-dark">{t.name}</p>
                      <p className="text-xs text-brand-muted">{t.loan_type} · {t.location}</p>
                    </div>
                    <div className="flex gap-0.5 text-brand-gold">
                      {Array.from({ length: t.rating }).map((_, s) => <Star key={s} size={13} fill="currentColor" />)}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* BLOG */}
      {blogs.length > 0 && (
        <section className="container-site py-20 lg:py-28" data-testid="blog-section">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
            <SectionHead eyebrow="Financial Education" title="Learn Before You Borrow"
              subtitle="Plain-language guides on loans, credit scores, EMIs and financial planning." />
            <Link to="/blog" data-testid="view-all-blog-btn" className="btn-outline shrink-0">All Articles <ArrowRight size={15} /></Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {blogs.map((b, i) => (
              <motion.div key={b.id} {...fade(i)}>
                <Link to={`/blog/${b.slug}`} data-testid={`blog-card-${b.slug}`} className="card-site group overflow-hidden flex flex-col h-full">
                  <div className="h-44 overflow-hidden">
                    <img src={b.image} alt={b.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-brand-red">{b.category}</span>
                    <h3 className="mt-2 font-heading text-lg font-bold text-brand-dark leading-snug flex-1">{b.title}</h3>
                    <p className="mt-2 text-xs text-brand-muted">{b.read_time} · {b.author}</p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="container-site pb-20 lg:pb-28" data-testid="faq-section">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <SectionHead eyebrow="FAQs" title="Questions, Answered Honestly"
              subtitle="Straight answers about loans, eligibility, documents and how we work." />
          </div>
          <div className="lg:col-span-8"><FAQAccordion faqs={faqs} testid="home-faq" /></div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="container-site pb-20 lg:pb-28" data-testid="final-cta-section">
        <motion.div {...fade()} className="rounded-3xl bg-brand-red text-white p-10 sm:p-16 text-center relative overflow-hidden">
          <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-white/10" />
          <div className="absolute -bottom-24 -left-16 w-72 h-72 rounded-full bg-black/10" />
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight relative">Find the Right Loan.<br />Apply With Confidence.</h2>
          <p className="mt-5 text-white/80 text-sm md:text-base max-w-xl mx-auto relative">Submit a quick enquiry and a dedicated relationship manager will help you explore suitable options from our lending partner network.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3 relative">
            <Link to="/apply" data-testid="final-apply-btn" className="btn-primary !bg-white !text-brand-red">Apply Now <ArrowRight size={15} /></Link>
            <a href={waLink(settings)} target="_blank" rel="noreferrer" data-testid="final-whatsapp-btn" className="btn-outline !bg-transparent !text-white !border-white/40 hover:!border-white">WhatsApp Us</a>
          </div>
        </motion.div>
      </section>
    </div>
  );
}
